import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { fetchData } from '../../../apiService';
import { Button } from '@material-ui/core';
import PrintIcon from '@mui/icons-material/Print';
import PurchaseOrderBill from './Bill';

const PurchaseOrderDetails = () => {
    const { id } = useParams();
    const [selectedItems, setSelectedItems] = useState([]);
    const { isAuthenticated, token, logout, companyId } = useAuth();
    const [formData, setFormData] = useState({});
    const [companyData, setCompanyData] = useState({});
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        fetchData('PurchaseOrderApi', `/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.PurchaseOrder);
                    setSelectedItems(response.response_data.PurchaseOrderDetails);
                }
            })
            .catch((error) => console.error('Error fetching Purchase Order Details:', error));

        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setCompanyData(response.response_data);
                }
            })
            .catch((error) => console.error('Error fetching company details:', error));
    }, [id]);

    const handlePrint = () => {
        const printContent = document.getElementById('purchase-order-print-area').innerHTML;
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><title>Purchase Order</title>
            <style>
                @page { margin: 8mm; size: A4; }
                body { margin: 0; font-family: Arial, sans-serif; }
                * { box-sizing: border-box; }
            </style>
        </head><body>${printContent}</body></html>`);
        doc.close();
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const filteredData = selectedItems.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div style={{ fontFamily: "Segoe UI, sans-serif", fontSize: "12px", width: "100%", padding: "5px", marginTop: '-10px' }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #ccc", paddingBottom: "6px", marginBottom: "12px" }}>
                <h6 className="uppercase tracking-wide font-serif text-black mt-2">Purchase Order Details</h6>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Supplier:</label>
                    <input type="text" value={formData.SupplierName || ''} readOnly
                        style={{ border: "1px solid #ccc", borderRadius: "4px", fontSize: "12px", height: "28px", width: "200px", padding: "0 6px" }} />
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                    <Button variant="contained" onClick={handlePrint}
                        style={{ height: "25px", minWidth: "90px", borderRadius: "6px", backgroundColor: "#1E3A8A", color: "white", fontWeight: 600, fontSize: "13px", textTransform: "none" }}>
                        <PrintIcon style={{ fontSize: '16px', marginRight: '4px' }} /> Print
                    </Button>
                    <Button variant="contained" component={Link} to="/PurchaseOrderIndex"
                        onClick={() => localStorage.setItem("shouldReload", "true")}
                        style={{ height: "25px", minWidth: "90px", borderRadius: "6px", backgroundColor: "#F3F4F6", color: "#1E3A8A", fontWeight: 600, fontSize: "13px", textTransform: "none" }}>
                        Back To List
                    </Button>
                </div>
            </div>

            {/* Form Fields */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px", marginBottom: "12px" }}>
                {[
                    { label: "Bill Date", name: "BillDate", type: "date", value: formatDateForInput(formData.BillDate) },
                    { label: "Bill Number", name: "BillNumber", type: "text", value: formData.BillNumber },
                    { label: "Round Off", name: "RoundOff", type: "number", value: formData.RoundOff },
                    { label: "Total", name: "TotalAmount", type: "number", value: formData.TotalAmount },
                ].map(({ label, name, type, value }) => (
                    <div key={name}>
                        <label style={{ fontSize: "11px", fontWeight: "600" }}>{label}</label>
                        <input type={type} name={name} value={value || ''} readOnly
                            style={{ border: "1px solid #ccc", borderRadius: "4px", fontSize: "12px", height: "28px", width: "100%", padding: "0 6px", textAlign: type === "number" ? "right" : "left", fontWeight: name === "TotalAmount" ? "bold" : "normal", color: name === "TotalAmount" ? "#0D47A1" : "inherit" }} />
                    </div>
                ))}
            </div>

            {/* Summary */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "55px", fontSize: "12px", alignItems: "center", marginBottom: "12px" }}>
                {[['Amount', formData.Amount], ['Discount', formData.DiscountAmount], ['Taxable', formData.TaxableAmount], ['CGST', formData.CGSTAmount], ['SGST', formData.SGSTAmount], ['IGST', formData.IGSTAmount], ['Round Off', formData.RoundOff]].map(([label, val]) => (
                    <span key={label}>{label}: <span style={{ color: "#B71C1C", fontWeight: "bold" }}>{val}</span></span>
                ))}
                <span style={{ fontSize: "13px", fontWeight: "bold" }}>Total: <span style={{ color: "#1565C0", fontWeight: "bold" }}>{formData.TotalAmount}</span></span>
            </div>

            {/* Items Table */}
            <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #999", borderRadius: "4px", marginBottom: "12px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead style={{ backgroundColor: "#1F2A38", color: "#FFFFFF" }}>
                        <tr>
                            {["SL.No", "Item", "Qty", "Rate", "Amount", "Disc%", "Disc₹", "Taxable", "GST%", "Bal.Qty", "Total"].map((h, i) => (
                                <th key={i} style={{ padding: "4px 6px", textAlign: i > 1 ? "right" : "left", fontSize: "10px", textTransform: "uppercase" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.map((item, index) => (
                            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#FDFDFD" : "#F5F7FA", borderBottom: "1px solid #ddd" }}>
                                <td style={{ padding: "4px 6px", textAlign: "center" }}>{index + 1}</td>
                                <td style={{ padding: "4px", minWidth: "160px" }}>{item.ItemName}</td>
                                <td style={{ padding: "2px", textAlign: "right" }}><input value={item.Quantity} readOnly style={{ width: "70px", textAlign: "right", border: "1px solid #bbb", borderRadius: "3px", padding: "2px" }} /></td>
                                <td style={{ padding: "2px", textAlign: "right" }}><input value={item.Rate || 0} readOnly style={{ width: "100px", textAlign: "right", border: "1px solid #bbb", borderRadius: "3px", padding: "2px" }} /></td>
                                <td style={{ padding: "2px", textAlign: "right" }}><input value={item.Amount || 0} readOnly style={{ width: "100px", textAlign: "right", border: "1px solid #ccc", borderRadius: "3px", padding: "2px" }} /></td>
                                <td style={{ padding: "2px", textAlign: "right" }}><input value={item.DiscountPercentage || 0} readOnly style={{ width: "70px", textAlign: "right", border: "1px solid #bbb", borderRadius: "3px", padding: "2px" }} /></td>
                                <td style={{ padding: "2px", textAlign: "right" }}><input value={item.DiscountAmount || 0} readOnly style={{ width: "100px", textAlign: "right", border: "1px solid #ccc", borderRadius: "3px", padding: "2px" }} /></td>
                                <td style={{ padding: "2px", textAlign: "right" }}><input value={item.TaxableAmount || 0} readOnly style={{ width: "100px", textAlign: "right", border: "1px solid #ccc", borderRadius: "3px", padding: "2px" }} /></td>
                                <td style={{ padding: "2px", textAlign: "right" }}><span style={{ color: "darkmagenta", fontSize: "11px" }}>{item.GSTRate || 0}%</span></td>
                                <td style={{ padding: "2px", textAlign: "right", width: "70px" }}><span style={{ color: "#2e7d32", fontWeight: "600", fontSize: "11px" }}>{item.BalanceQuantity || 0}</span></td>
                                <td style={{ padding: "2px", textAlign: "right" }}><input value={item.TotalAmount || 0} readOnly style={{ width: "100px", textAlign: "right", border: "1px solid #888", borderRadius: "3px", padding: "2px", fontWeight: "bold" }} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {formData.Remarks && (
                <div style={{ marginTop: "8px" }}>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Remarks: </label>
                    <span style={{ fontSize: "12px" }}>{formData.Remarks}</span>
                </div>
            )}

            {/* Hidden Print Area */}
            <div style={{ display: 'none' }}>
                <div id="purchase-order-print-area">
                    <PurchaseOrderBill labelData={{
                        PurchaseOrder: {
                            ...formData,
                            CompanyName: companyData.Name,
                            CompanyAddressLine: `${companyData.AddressLine1 || ''} ${companyData.AddressLine2 || ''}`.trim(),
                            CompanyContactNumber1: companyData.ContactNumber1,
                            CompanyGSTNumber: companyData.GSTNumber,
                        },
                        PurchaseOrderDetails: selectedItems
                    }} />
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderDetails;
